import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useNavigate } from 'react-router-dom';
import { usePdf } from '../../context/PdfContext';
import { OutputFormat } from '../../types';
import { computeTextStats, countMatches } from '../../utils/textStats';
import HighlightedText from './HighlightedText';
import MarkdownContent from '../MarkdownContent/MarkdownContent';
import { EDITOR_PATH, stashTextForEditor } from '../../utils/editor/handoff';

const TextOutput: React.FC = () => {
  const {
    files,
    currentFileId,
    batchInfo,
    outputFormat,
    setOutputFormat,
    ensureMarkdown,
    getSelectedFilesMarkdown,
  } = usePdf();

  const currentFile = files.find((file) => file.id === currentFileId);
  const multipleSelected = files.filter(f => f.selected).length > 1;
  const isMarkdown = outputFormat === 'markdown';

  const [copied, setCopied] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // When in Markdown mode, lazily convert the current file once it has text.
  useEffect(() => {
    if (
      isMarkdown &&
      currentFile &&
      !currentFile.markdown &&
      !currentFile.isProcessing &&
      !currentFile.error
    ) {
      ensureMarkdown(currentFile.id).catch(() => {/* error surfaced on file */});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMarkdown, currentFile?.id, currentFile?.markdown, currentFile?.isProcessing]);

  const handleFormatChange = (
    _e: React.MouseEvent<HTMLElement>,
    value: OutputFormat | null
  ) => {
    if (value) setOutputFormat(value);
  };

  // Resolve the text that should be copied / shown for the current view.
  const batchContent = batchInfo
    ? (isMarkdown ? getSelectedFilesMarkdown() : batchInfo.combinedText)
    : '';
  const singleContent = currentFile
    ? (isMarkdown ? (currentFile.markdown || '') : currentFile.text)
    : '';

  const activeContent = multipleSelected && batchInfo ? batchContent : singleContent;

  const handleCopy = () => {
    if (!activeContent) return;
    navigator.clipboard.writeText(activeContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenInEditor = () => {
    if (activeContent && stashTextForEditor(activeContent)) navigate(EDITOR_PATH);
  };

  const openInEditorButton = (
    <Tooltip title="Open in Text Editor">
      <span>
        <IconButton onClick={handleOpenInEditor} disabled={!activeContent} size="small" aria-label="Open in Text Editor">
          <EditNoteIcon />
        </IconButton>
      </span>
    </Tooltip>
  );

  const formatToggle = (
    <ToggleButtonGroup
      value={outputFormat}
      exclusive
      size="small"
      onChange={handleFormatChange}
      aria-label="Output format"
    >
      <ToggleButton value="text" aria-label="Plain text">Text</ToggleButton>
      <ToggleButton value="markdown" aria-label="Markdown">Markdown</ToggleButton>
    </ToggleButtonGroup>
  );

  // Document statistics + in-output search, shared by single & batch views.
  const statsBar = (content: string) => {
    const s = computeTextStats(content);
    const matches = searchQuery ? countMatches(content, searchQuery) : 0;
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" label={`${s.words.toLocaleString()} words`} />
          <Chip size="small" variant="outlined" label={`${s.characters.toLocaleString()} chars`} />
          <Chip size="small" variant="outlined" label={`${s.lines.toLocaleString()} lines`} />
          <Chip size="small" variant="outlined" label={`~${s.pages} pages`} />
          <Chip size="small" variant="outlined" label={`~${s.readingMinutes} min read`} />
        </Stack>
        <TextField
          size="small"
          placeholder="Search in output…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <Typography variant="caption" sx={{ whiteSpace: 'nowrap', mr: 0.5 }}>
                  {matches} match{matches === 1 ? '' : 'es'}
                </Typography>
                <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ minWidth: { xs: '100%', sm: 260 } }}
        />
      </Stack>
    );
  };

  // Plain-text body: switches to a highlighted view while searching.
  const renderTextBody = (content: string, minRows: number) => {
    if (searchQuery) {
      return <HighlightedText content={content || ''} query={searchQuery} />;
    }
    return (
      <TextField
        multiline
        fullWidth
        minRows={minRows}
        maxRows={30}
        value={content || 'No text extracted yet'}
        InputProps={{
          readOnly: true,
          sx: { fontFamily: 'monospace', fontSize: '0.9rem' },
        }}
        variant="outlined"
      />
    );
  };

  const markdownPreview = (content: string) => (
    showRaw ? (
      renderTextBody(content, 18)
    ) : (
      <MarkdownContent
        content={content || '_No content yet_'}
        sx={{
          p: 2,
          minHeight: 320,
          maxHeight: 640,
          overflow: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      />
    )
  );

  const rawPreviewToggle = (
    <ToggleButtonGroup
      value={showRaw ? 'raw' : 'preview'}
      exclusive
      size="small"
      onChange={(_e, v) => v && setShowRaw(v === 'raw')}
    >
      <ToggleButton value="preview">Preview</ToggleButton>
      <ToggleButton value="raw">Raw</ToggleButton>
    </ToggleButtonGroup>
  );

  if (!currentFile) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Document Selected
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please upload and select a PDF, Word or Excel file to view the converted output
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (multipleSelected && batchInfo) {
    return (
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
            {isMarkdown ? 'Combined Markdown' : 'Combined Text'}
            <Chip
              icon={<FileCopyIcon />}
              label={`${batchInfo.processedFiles} files`}
              color="primary"
              size="small"
              sx={{ ml: 1 }}
            />
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {formatToggle}
            {openInEditorButton}
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'} placement="top">
              <IconButton onClick={handleCopy} color={copied ? 'success' : 'default'} size="small">
                {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
        {isMarkdown && <Box sx={{ mb: 1 }}>{rawPreviewToggle}</Box>}
        {statsBar(batchContent)}
        {isMarkdown ? markdownPreview(batchContent) : renderTextBody(batchContent, 15)}
      </Paper>
    );
  }

  if (currentFile.isProcessing) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box>
          <CircularProgress size={50} sx={{ mb: 3 }} />
          <Typography variant="h6" gutterBottom>
            Processing Document
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isMarkdown ? 'Converting to Markdown…' : 'Extracting text from your document…'}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (currentFile.error) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box>
          <Typography variant="h6" color="error" gutterBottom>
            Error Processing Document
          </Typography>
          <Typography variant="body1" color="error">
            {currentFile.error}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            Try uploading a different file
          </Typography>
        </Box>
      </Paper>
    );
  }

  const content = singleContent;

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
            {isMarkdown ? 'Markdown Output' : 'Extracted Text'}
          </Typography>
          {isMarkdown && currentFile.fileType === 'pdf' && (
            <Chip label="best-effort formatting" size="small" color="warning" variant="outlined" />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {formatToggle}
          {openInEditorButton}
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <IconButton onClick={handleCopy} color={copied ? 'success' : 'default'} size="small">
              {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 1 }} />
      {isMarkdown && <Box sx={{ mb: 1 }}>{rawPreviewToggle}</Box>}
      {statsBar(content)}
      {isMarkdown ? markdownPreview(content) : renderTextBody(content, 18)}
    </Paper>
  );
};

export default TextOutput;
